// Port of BivouacEngine/BivouacZoneGenerator.swift (ZoneSearchContext, BivouacZoneGenerator and its internal types).

import type {
  BivouacCandidate,
  BivouacReason,
  Confidence,
  EpochMs,
  GeoBounds,
  GeoCoordinate,
  GeoDataQuerying,
  GeoPolygon,
  POICategory,
  SuitabilityStatus,
  TerrainSample,
  UserReport,
  WeatherForecast,
  WeatherSnapshot,
} from '../core/models';
import {
  combineRegulatoryStates,
  effectImpliedState,
  minConfidence,
  protectedAreaDistance,
  regulatorySeverity,
} from '../core/domain';
import {
  boundsExpanded,
  boundsOf,
  circlePolygon,
  convexHull,
  distance,
  isValidCoordinate,
  openRing,
  polygonBounds,
} from '../core/geo';
import { localCalendar } from '../nav/calendar';
import type { RouteTrack } from '../nav/routeTrack';
import { BivouacLog } from './bivouacLog';
import {
  OvernightFacilities,
  POIProximity,
  PolygonOverlap,
  TerrainLattice,
  WeatherAdjustment,
  containsReason,
  reasonKindEquals,
  sensitivityAggregationRank,
  sliceTrack,
} from './engineSupport';
import { RegulationResolver, type RegulatoryAssessment } from './regulationResolver';
import { SuitabilityClassifier } from './suitabilityClassifier';
import { SuitabilityEvaluator, type PhysicalAssessment, type WeatherAssessment } from './suitabilityEvaluator';

/** Parameters of a zone search along a route (omitted fields take the Swift defaults). */
export interface ZoneSearchContext {
  /** Night being assessed (regulatory seasons, avalanche season). */
  date: EpochMs;
  /** Straight-line distance from the route (m) beyond which cells are not considered. Default 1000. */
  maxDetourMeters?: number;
  /** Forecast used for the weather score (snapshot at the estimated arrival, lapse-rate adjusted to each cell). */
  forecast?: WeatherForecast;
  /** Estimated arrival at a coordinate (for the forecast snapshot); `date` is used when absent. */
  arrivalEstimate?: (coordinate: GeoCoordinate) => EpochMs;
  /** User observations (only `regulationSignObserved` is used; it can only add caution). Default []. */
  userReports?: readonly UserReport[];
  /** Minimum radius of every zone (m), floored at `minimumAllowedZoneRadiusMeters`. Default 200. */
  minimumZoneRadiusMeters?: number;
  /** Maximum number of zones returned (the best ones), to keep the UI calm. Default 8. */
  maximumZoneCount?: number;
}

/** Status, sensitivity, confidence and reasons derived from the three assessments of a location. */
export interface CombinedAssessment {
  status: SuitabilityStatus;
  sensitivity: BivouacCandidate['environmentalSensitivity'];
  confidence: Confidence;
  reasons: BivouacReason[];
}

/** One evaluated terrain cell. */
export interface CellEvaluation {
  sample: TerrainSample;
  distanceFromTrail?: number;
  /** Distance along the full route of the cell's projection (zone search only). */
  distanceAlong?: number;
  physical: PhysicalAssessment;
  regulatory: RegulatoryAssessment;
  weather?: WeatherAssessment;
  status: SuitabilityStatus;
  sensitivity: BivouacCandidate['environmentalSensitivity'];
  confidence: Confidence;
  reasons: BivouacReason[];
}

/** A zone with the separate confidences needed by the Sleep Tonight planner. */
export interface EvaluatedZone {
  candidate: BivouacCandidate;
  regulatoryConfidence: Confidence;
  terrainConfidence: Confidence;
}

/** Water sources and official accommodation prefetched once for a search area. */
export class FacilityLookup {
  readonly water: POIProximity;
  readonly accommodation: POIProximity;

  constructor(data: GeoDataQuerying, bounds: GeoBounds) {
    const expanded = boundsExpanded(bounds, BivouacZoneGenerator.waterSearchRadiusMeters);
    const categories = new Set(OvernightFacilities.categories);
    categories.add('water');
    const pois = data.pointsOfInterestIn(expanded, categories);
    this.water = new POIProximity(pois.filter((p) => p.category === 'water'));
    this.accommodation = new POIProximity(pois.filter((p) => OvernightFacilities.isOfficial(p)));
  }
}

interface CellInput {
  sample: TerrainSample;
  distanceFromTrail: number | undefined;
  maxDetourMeters: number;
  facilities: FacilityLookup;
  data: GeoDataQuerying;
  date: EpochMs;
  snapshot: WeatherSnapshot | undefined;
  userReports: readonly UserReport[];
  terrainConfidence: Confidence;
  containsSyntheticData: boolean;
}

const waterCategory: ReadonlySet<POICategory> = new Set<POICategory>(['water']);
const compareStrings = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
/** Swift `Double.rounded()` (half away from zero). */
const roundHalfAwayFromZero = (x: number) => Math.sign(x) * Math.round(Math.abs(x));

/**
 * Generates generalised SEARCH ZONES (never exact pitches): evaluates terrain samples within maxDetour of the track
 * between two distances, clusters adjacent cells of equal status (suitable/caution) into zones (convex hull, buffered
 * so the zone is at least `minimumZoneRadiusMeters` in radius), and returns BivouacCandidates sorted by status then
 * score. Red/grey cells are returned only by `overlay` (for the map layer), not as destinations.
 *
 * Generalisation (DECISIONS.md D-008):
 * - cells are clustered by 8-neighbourhood on the terrain grid (neighbours ≤ 1.5 × inferred cell spacing apart), and
 *   clusters longer than `maximumZoneExtentMeters` along the route are cut into equal stretches;
 * - the zone centre is the cluster centroid rounded to 0.001° (≈ 100 m), so no single cell's exact position is ever
 *   output as a destination; the candidate id is `zone:<lat>:<lon>` of that rounded centre (stable);
 * - the zone polygon is the convex hull of the member cells' corners **and** a circle of the minimum radius around the
 *   centre;
 * - a zone whose polygon reaches into an active protected area or regulation polygon stricter than "no known
 *   restriction", or into land outside regulatory coverage, is downgraded to caution with the matching reasons;
 * - at most `maximumZoneCount` zones are returned.
 */
export class BivouacZoneGenerator {
  /** Default number of zones returned by `zones`. */
  static readonly defaultMaximumZoneCount = 8;
  /** Floor applied to `ZoneSearchContext.minimumZoneRadiusMeters`. */
  static readonly minimumAllowedZoneRadiusMeters = 100;
  /** Radius (m) of the area drawn around a spot check. */
  static readonly spotCheckRadiusMeters = 100;
  /** Water POIs are looked up within this radius (m). */
  static readonly waterSearchRadiusMeters = 2000;
  /** Official accommodation is mentioned within this radius (m). */
  static readonly accommodationSearchRadiusMeters = 1000;
  /** Clusters longer than this along the route (m) are cut into equal stretches. */
  static readonly maximumZoneExtentMeters = 1000;
  /** Vertices of the zone circle. */
  static readonly circleSegments = 24;

  /** Scoring of terrain and weather. */
  readonly evaluator: SuitabilityEvaluator;
  /** Regulatory resolution. */
  readonly resolver: RegulationResolver;

  constructor(
    evaluator: SuitabilityEvaluator = new SuitabilityEvaluator(),
    resolver: RegulationResolver = new RegulationResolver(),
  ) {
    this.evaluator = evaluator;
    this.resolver = resolver;
  }

  // MARK: Public API

  /** Search zones (status `suitable` or `caution` only) along `track` between two distances. */
  zones(
    track: RouteTrack,
    startDistance: number,
    endDistance: number,
    data: GeoDataQuerying,
    context: ZoneSearchContext,
  ): BivouacCandidate[] {
    return this.evaluatedZones(track, startDistance, endDistance, data, context).map((z) => z.candidate);
  }

  /**
   * One candidate per terrain cell in `bounds` (small square polygon), including unsuitable and insufficient-data
   * cells, for the suitability map layer. Sorted by latitude, then longitude.
   */
  overlay(bounds: GeoBounds, data: GeoDataQuerying, date: EpochMs, forecast?: WeatherForecast): BivouacCandidate[] {
    const samples = data.terrainSamplesIn(bounds).filter((s) => isValidCoordinate(s.coordinate));
    if (samples.length === 0) return [];
    const spacing = TerrainLattice.spacing(samples.map((s) => s.coordinate)) ?? TerrainLattice.fallbackSpacingMeters;
    const facilities = new FacilityLookup(data, bounds);
    const terrainConfidence = BivouacZoneGenerator.terrainConfidence(data);
    const ordered = samples
      .slice()
      .sort((a, b) => a.coordinate.latitude - b.coordinate.latitude || a.coordinate.longitude - b.coordinate.longitude);
    return ordered.map((sample) => {
      const snapshot = WeatherAdjustment.snapshot(forecast, date, sample.elevation);
      const cell = this.evaluateCell({
        sample,
        distanceFromTrail: undefined,
        maxDetourMeters: Infinity,
        facilities,
        data,
        date,
        snapshot,
        userReports: [],
        terrainConfidence,
        containsSyntheticData: data.containsSyntheticData,
      });
      return {
        id: `cell:${sample.coordinate.latitude.toFixed(5)}:${sample.coordinate.longitude.toFixed(5)}`,
        area: { exterior: TerrainLattice.cellCorners(sample.coordinate, spacing), holes: [] },
        center: sample.coordinate,
        physicalScore: cell.physical.score,
        regulatoryState: cell.regulatory.state,
        weatherScore: cell.weather?.score,
        environmentalSensitivity: cell.sensitivity,
        confidence: cell.confidence,
        reasons: cell.reasons,
        status: cell.status,
        elevation: sample.elevation,
      };
    });
  }

  /**
   * "Check this spot" (long-press on the map): evaluates the terrain cell containing `coordinate` (insufficient data
   * when no cell covers it) and the regulatory situation at `coordinate` itself. `forecast` is the snapshot to score.
   */
  evaluate(
    coordinate: GeoCoordinate,
    data: GeoDataQuerying,
    date: EpochMs,
    distanceFromTrail?: number,
    forecast?: WeatherSnapshot,
  ): BivouacCandidate {
    const sample = this.nearestSample(coordinate, data);
    let water: number | undefined;
    const waterRadius = BivouacZoneGenerator.waterSearchRadiusMeters;
    for (const poi of data.pointsOfInterestNear(coordinate, waterRadius, waterCategory)) {
      const d = distance(coordinate, poi.coordinate);
      if (water === undefined || d < water) water = d;
    }
    let accommodation: { name: string; distance: number } | undefined;
    for (const poi of data.pointsOfInterestNear(
      coordinate,
      BivouacZoneGenerator.accommodationSearchRadiusMeters,
      OvernightFacilities.categories,
    )) {
      if (!OvernightFacilities.isOfficial(poi)) continue;
      const entry = { name: poi.name, distance: distance(coordinate, poi.coordinate) };
      if (
        !accommodation ||
        entry.distance < accommodation.distance ||
        (entry.distance === accommodation.distance && entry.name < accommodation.name)
      ) {
        accommodation = entry;
      }
    }
    const physical = this.evaluator.physical({
      sample,
      distanceFromTrail,
      maxDetourMeters: Infinity,
      nearbyWaterDistance: water,
      nearbyAccommodation: accommodation,
      date,
    });
    const regulatory = this.resolver.assess(coordinate, date, data);
    const weather = this.evaluator.weather(forecast, sample?.windExposure ?? 'unknown');
    const cell = this.assemble(
      physical,
      regulatory,
      weather,
      BivouacZoneGenerator.terrainConfidence(data),
      data.containsSyntheticData,
    );
    return {
      id: `spot:${coordinate.latitude.toFixed(5)}:${coordinate.longitude.toFixed(5)}`,
      area: circlePolygon(coordinate, BivouacZoneGenerator.spotCheckRadiusMeters),
      center: coordinate,
      physicalScore: physical.score,
      regulatoryState: regulatory.state,
      weatherScore: weather?.score,
      environmentalSensitivity: cell.sensitivity,
      confidence: cell.confidence,
      reasons: cell.reasons,
      status: cell.status,
      elevation: sample?.elevation,
    };
  }

  // MARK: Zones

  /** Zones with the confidences the planner needs (Swift `internal`, used by `SleepTonightPlanner`). */
  evaluatedZones(
    track: RouteTrack,
    startDistance: number,
    endDistance: number,
    data: GeoDataQuerying,
    context: ZoneSearchContext,
  ): EvaluatedZone[] {
    const lower = Math.max(0, Math.min(startDistance, endDistance));
    const upper = Math.min(track.totalDistance, Math.max(startDistance, endDistance));
    const maxDetour = context.maxDetourMeters ?? 1000;
    const maximumZoneCount = context.maximumZoneCount ?? BivouacZoneGenerator.defaultMaximumZoneCount;
    const userReports = context.userReports ?? [];
    if (!Number.isFinite(maxDetour) || maxDetour < 0 || !(maximumZoneCount > 0)) return [];
    const slice = sliceTrack(track, lower, upper);
    if (!slice) return [];
    const routeBounds = boundsOf(slice.coordinates);
    if (!routeBounds) return [];
    const searchBounds = boundsExpanded(routeBounds, maxDetour);
    const samples = data.terrainSamplesIn(searchBounds).filter((s) => isValidCoordinate(s.coordinate));
    if (samples.length === 0) {
      BivouacLog.logger.info('No terrain samples along the requested stretch; no bivouac zones');
      return [];
    }

    const facilities = new FacilityLookup(data, searchBounds);
    const terrainConfidence = BivouacZoneGenerator.terrainConfidence(data);
    const containsSynthetic = data.containsSyntheticData;
    const cells: CellEvaluation[] = [];
    for (const sample of samples) {
      const projection = slice.project(sample.coordinate);
      if (!projection || !(projection.crossTrackDistance <= maxDetour)) continue;
      // The arrival only selects the forecast snapshot: without a forecast the (route-projecting, hence costly)
      // estimate is skipped, which changes nothing in the result.
      const arrival = context.forecast ? (context.arrivalEstimate?.(sample.coordinate) ?? context.date) : context.date;
      const snapshot = WeatherAdjustment.snapshot(context.forecast, arrival, sample.elevation);
      const cell = this.evaluateCell({
        sample,
        distanceFromTrail: projection.crossTrackDistance,
        maxDetourMeters: maxDetour,
        facilities,
        data,
        date: context.date,
        snapshot,
        userReports,
        terrainConfidence,
        containsSyntheticData: containsSynthetic,
      });
      cell.distanceAlong = lower + projection.distanceAlong;
      if (cell.status === 'suitable' || cell.status === 'caution') cells.push(cell);
    }
    if (cells.length === 0) return [];

    const spacing = TerrainLattice.spacing(samples.map((s) => s.coordinate)) ?? TerrainLattice.fallbackSpacingMeters;
    const connected = TerrainLattice.clusters(
      cells.map((c) => c.sample.coordinate),
      cells.map((c) => (c.status === 'suitable' ? 0 : 1)),
      spacing,
    );
    const clusters = connected.flatMap((cluster) => BivouacZoneGenerator.splitAlongRoute(cluster, cells, spacing));
    const requested = context.minimumZoneRadiusMeters ?? 200;
    const requestedRadius = Number.isFinite(requested) ? requested : 0;
    const radius = Math.max(BivouacZoneGenerator.minimumAllowedZoneRadiusMeters, requestedRadius);
    const zones = clusters.map((indices) =>
      this.flaggingRestrictedOverlap(
        this.makeZone(
          indices.map((i) => cells[i]),
          spacing,
          radius,
          terrainConfidence,
        ),
        data,
        context.date,
      ),
    );
    zones.sort((a, b) => {
      const ra = a.candidate.status === 'suitable' ? 0 : 1;
      const rb = b.candidate.status === 'suitable' ? 0 : 1;
      if (ra !== rb) return ra - rb;
      if (a.candidate.physicalScore !== b.candidate.physicalScore) {
        return b.candidate.physicalScore - a.candidate.physicalScore;
      }
      return compareStrings(a.candidate.id, b.candidate.id);
    });
    // Two clusters can round to the same centre: keep ids unique and deterministic.
    const seen = new Map<string, number>();
    for (const zone of zones) {
      const id = zone.candidate.id;
      const count = seen.get(id) ?? 0;
      seen.set(id, count + 1);
      if (count > 0) zone.candidate.id = `${id}-${count + 1}`;
    }
    BivouacLog.logger.debug(`Zone search: ${cells.length} destination cells in ${clusters.length} clusters`);
    return zones.slice(0, maximumZoneCount);
  }

  /**
   * Splits a cluster longer than `maximumZoneExtentMeters` along the route into equal stretches (each re-checked for
   * connectivity), so no zone spans more than about a kilometre of trail and no tiny fragment is created at an
   * arbitrary boundary.
   */
  static splitAlongRoute(cluster: readonly number[], cells: readonly CellEvaluation[], spacing: number): number[][] {
    if (cluster.length === 0) return [];
    const alongs = cluster.map((i) => cells[i].distanceAlong ?? 0);
    let lowest = Infinity;
    let highest = -Infinity;
    for (const a of alongs) {
      if (a < lowest) lowest = a;
      if (a > highest) highest = a;
    }
    const extent = highest - lowest;
    if (!(extent > BivouacZoneGenerator.maximumZoneExtentMeters)) return [cluster.slice()];
    const pieces = Math.ceil(extent / BivouacZoneGenerator.maximumZoneExtentMeters);
    const pieceLength = extent / pieces;
    const stretch = alongs.map((a) => Math.min(pieces - 1, Math.trunc((a - lowest) / pieceLength)));
    const parts = TerrainLattice.clusters(
      cluster.map((i) => cells[i].sample.coordinate),
      stretch,
      spacing,
    );
    return parts.map((part) => part.map((i) => cluster[i]).sort((a, b) => a - b));
  }

  makeZone(
    members: readonly CellEvaluation[],
    spacing: number,
    minimumRadius: number,
    terrainConfidence: Confidence,
  ): EvaluatedZone {
    const count = members.length;
    let latitudeSum = 0;
    let longitudeSum = 0;
    for (const m of members) {
      latitudeSum += m.sample.coordinate.latitude;
      longitudeSum += m.sample.coordinate.longitude;
    }
    const centroid: GeoCoordinate = { latitude: latitudeSum / count, longitude: longitudeSum / count };
    const center = BivouacZoneGenerator.generalised(centroid);
    const corners = members.flatMap((m) => TerrainLattice.cellCorners(m.sample.coordinate, spacing));
    // Circumscribe the circle so the polygon's edges (not only its vertices) are at least `minimumRadius` away.
    const segments = BivouacZoneGenerator.circleSegments;
    const circleRadius = minimumRadius / Math.cos(Math.PI / segments);
    const circle = circlePolygon(center, circleRadius, segments).exterior;
    const area: GeoPolygon = { exterior: convexHull([...corners, ...circle]), holes: [] };

    let representative = members[0];
    for (const candidate of members.slice(1)) {
      if (BivouacZoneGenerator.isBetterRepresentative(candidate, representative)) representative = candidate;
    }
    const reasons = representative.reasons.slice();
    for (const member of members) {
      for (const r of member.reasons) {
        if (!BivouacZoneGenerator.isMergeable(r)) continue;
        if (!reasons.some((existing) => BivouacZoneGenerator.sameTopic(existing, r))) reasons.push(r);
      }
    }

    const weatherScores = members.flatMap((m) => (m.weather ? [m.weather.score] : []));
    let sensitivity = members[0].sensitivity;
    for (const m of members) {
      if (sensitivityAggregationRank(m.sensitivity) > sensitivityAggregationRank(sensitivity)) {
        sensitivity = m.sensitivity;
      }
    }
    const candidate: BivouacCandidate = {
      id: `zone:${center.latitude.toFixed(3)}:${center.longitude.toFixed(3)}`,
      area,
      center,
      physicalScore: members.reduce((sum, m) => sum + m.physical.score, 0) / count,
      regulatoryState: combineRegulatoryStates(members.map((m) => m.regulatory.state)),
      weatherScore: weatherScores.length > 0 ? Math.min(...weatherScores) : undefined,
      environmentalSensitivity: sensitivity,
      confidence: minConfidence(...members.map((m) => m.confidence)),
      reasons,
      status: representative.status,
      elevation: members.reduce((sum, m) => sum + m.sample.elevation, 0) / count,
    };
    return {
      candidate,
      regulatoryConfidence: minConfidence(...members.map((m) => m.regulatory.confidence)),
      terrainConfidence,
    };
  }

  /** Representative cell order: best physical score, then nearest to the trail, then latitude, then longitude. */
  private static isBetterRepresentative(a: CellEvaluation, b: CellEvaluation): boolean {
    if (a.physical.score !== b.physical.score) return a.physical.score > b.physical.score;
    const da = a.distanceFromTrail ?? Infinity;
    const db = b.distanceFromTrail ?? Infinity;
    if (da !== db) return da < db;
    if (a.sample.coordinate.latitude !== b.sample.coordinate.latitude) {
      return a.sample.coordinate.latitude < b.sample.coordinate.latitude;
    }
    return a.sample.coordinate.longitude < b.sample.coordinate.longitude;
  }

  /**
   * Generalisation must never paint a search area over a restriction, a caveat or missing data: the buffered hull of a
   * concave cluster (or the minimum-radius circle) can reach into a protected area or regulation polygon whose state is
   * stricter than "no known restriction", or into land outside regulatory coverage, that no member cell touches. Such
   * zones get the matching caution reasons, at least `caution` regulatory state, and are never presented as
   * `suitable`. Protected areas are tested by exact polygon intersection; regulation scopes and coverage (only
   * queryable by point) are sampled at the zone's vertices. Restrictions count when they are active at some point of
   * the night.
   */
  flaggingRestrictedOverlap(zone: EvaluatedZone, data: GeoDataQuerying, date: EpochMs): EvaluatedZone {
    const ring = zone.candidate.area.exterior;
    const bounds = polygonBounds(zone.candidate.area);
    if (!bounds) return zone;
    const flags: BivouacReason[] = [];
    const calendar = localCalendar;
    const noKnownRestriction = regulatorySeverity('noKnownRestriction');
    const areas = data
      .protectedAreasIn(bounds)
      .slice()
      .sort((a, b) => compareStrings(a.id, b.id));
    for (const area of areas) {
      if (!RegulationResolver.isActiveDuringNight(area, date, calendar)) continue;
      // Anything stricter than "no known restriction" (caution, unknown, restricted, prohibited) counts.
      if (!(regulatorySeverity(RegulationResolver.activeState(area)) > noKnownRestriction)) continue;
      if (!area.polygons.some((p) => PolygonOverlap.intersects(ring, p))) continue;
      flags.push({
        kind: {
          kind: 'nearProtectedArea',
          name: area.name,
          meters: protectedAreaDistance(area, zone.candidate.center),
        },
        impact: 'caution',
        category: 'regulation',
      });
    }
    const seenRegulations = new Set<string>();
    let leavesCoverage = false;
    for (const vertex of openRing(ring)) {
      if (!leavesCoverage && data.regulatoryCoverageAt(vertex).length === 0) leavesCoverage = true;
      for (const regulation of data.regulationsApplicableAt(vertex)) {
        if (!(regulatorySeverity(effectImpliedState(regulation.effect)) > noKnownRestriction)) continue;
        if (!RegulationResolver.isInEffectDuringNight(regulation, date, calendar)) continue;
        if (seenRegulations.has(regulation.id)) continue;
        seenRegulations.add(regulation.id);
        const isSynthetic = regulation.isSynthetic || regulation.provenance.isSynthetic;
        flags.push({
          kind: { kind: 'regulation', title: regulation.title, effect: regulation.effect, isSynthetic },
          impact: 'caution',
          category: 'regulation',
        });
      }
    }
    if (leavesCoverage) {
      flags.push({ kind: { kind: 'outsideRegulatoryCoverage' }, impact: 'caution', category: 'regulation' });
    }
    if (flags.length === 0) return zone;
    const reasons = zone.candidate.reasons.slice();
    for (const flag of flags) {
      if (!reasons.some((existing) => BivouacZoneGenerator.sameTopic(existing, flag))) reasons.push(flag);
    }
    return {
      ...zone,
      candidate: {
        ...zone.candidate,
        reasons,
        regulatoryState: combineRegulatoryStates([zone.candidate.regulatoryState, 'caution']),
        status: zone.candidate.status === 'suitable' ? 'caution' : zone.candidate.status,
      },
    };
  }

  // MARK: Cells

  evaluateCell(input: CellInput): CellEvaluation {
    const { sample, data, date } = input;
    const water = input.facilities.water.nearest(
      sample.coordinate,
      BivouacZoneGenerator.waterSearchRadiusMeters,
    )?.distance;
    const nearestAccommodation = input.facilities.accommodation.nearest(
      sample.coordinate,
      BivouacZoneGenerator.accommodationSearchRadiusMeters,
    );
    const physical = this.evaluator.physical({
      sample,
      distanceFromTrail: input.distanceFromTrail,
      maxDetourMeters: input.maxDetourMeters,
      nearbyWaterDistance: water,
      nearbyAccommodation: nearestAccommodation
        ? { name: nearestAccommodation.poi.name, distance: nearestAccommodation.distance }
        : undefined,
      date,
    });
    const regulatory = this.resolver.assess(sample.coordinate, date, data, input.userReports);
    const weather = this.evaluator.weather(input.snapshot, sample.windExposure);
    const combined = this.assemble(physical, regulatory, weather, input.terrainConfidence, input.containsSyntheticData);
    return {
      sample,
      distanceFromTrail: input.distanceFromTrail,
      distanceAlong: undefined,
      physical,
      regulatory,
      weather,
      status: combined.status,
      sensitivity: combined.sensitivity,
      confidence: combined.confidence,
      reasons: combined.reasons,
    };
  }

  /**
   * Combines the three assessments: status (`SuitabilityClassifier`), sensitivity (raised to high inside or near
   * protected areas), confidence = min(terrain confidence, regulatory confidence) and de-duplicated reasons.
   */
  assemble(
    physical: PhysicalAssessment,
    regulatory: RegulatoryAssessment,
    weather: WeatherAssessment | undefined,
    terrainConfidence: Confidence,
    containsSyntheticData: boolean,
  ): CombinedAssessment {
    const status = SuitabilityClassifier.status(physical, regulatory.state, weather?.score);
    const sensitivity = regulatory.protectedAreas.length === 0 ? physical.sensitivity : 'high';
    const confidence = minConfidence(physical.hasTerrainData ? terrainConfidence : 'low', regulatory.confidence);
    const all = [...physical.reasons, ...regulatory.reasons, ...(weather?.reasons ?? [])];
    if (containsSyntheticData) {
      all.push({ kind: { kind: 'syntheticData' }, impact: 'caution', category: 'dataQuality' });
    }
    const reasons: BivouacReason[] = [];
    for (const r of all) if (!containsReason(reasons, r)) reasons.push(r);
    return { status, sensitivity, confidence, reasons };
  }

  /** Terrain sample of the cell containing `coordinate`, if the grid covers it. */
  nearestSample(coordinate: GeoCoordinate, data: GeoDataQuerying): TerrainSample | undefined {
    if (!isValidCoordinate(coordinate)) return undefined;
    const point = boundsOf([coordinate]);
    if (!point) return undefined;
    const samples = data.terrainSamplesIn(boundsExpanded(point, 750)).filter((s) => isValidCoordinate(s.coordinate));
    const spacing = TerrainLattice.spacing(samples.map((s) => s.coordinate)) ?? 150;
    let nearest: { sample: TerrainSample; distance: number } | undefined;
    for (const sample of samples) {
      const d = distance(coordinate, sample.coordinate);
      if (
        !nearest ||
        d < nearest.distance ||
        (d === nearest.distance &&
          (sample.coordinate.latitude < nearest.sample.coordinate.latitude ||
            (sample.coordinate.latitude === nearest.sample.coordinate.latitude &&
              sample.coordinate.longitude < nearest.sample.coordinate.longitude)))
      ) {
        nearest = { sample, distance: d };
      }
    }
    if (!nearest || !(nearest.distance <= spacing)) return undefined;
    return nearest.sample;
  }

  // MARK: Helpers

  /**
   * Terrain-derived assessments are never better than `medium` (cell-level analysis cannot see micro-relief, boulders
   * or livestock) and `low` whenever the data contains synthetic values.
   */
  static terrainConfidence(data: GeoDataQuerying): Confidence {
    return data.containsSyntheticData ? 'low' : 'medium';
  }

  /** Centroid rounded to 0.001° — the published zone centre. */
  static generalised(c: GeoCoordinate): GeoCoordinate {
    return {
      latitude: roundHalfAwayFromZero(c.latitude * 1000) / 1000,
      longitude: roundHalfAwayFromZero(c.longitude * 1000) / 1000,
    };
  }

  /** Reasons from non-representative cells that must still be shown for the zone (they can explain its state). */
  static isMergeable(r: BivouacReason): boolean {
    if (r.impact !== 'caution' && r.impact !== 'negative' && r.impact !== 'blocking') return false;
    return r.category === 'regulation' || r.category === 'environment' || r.category === 'dataQuality';
  }

  /** Reasons about the same thing with different measurements count once. */
  static sameTopic(a: BivouacReason, b: BivouacReason): boolean {
    if (a.kind.kind === 'nearProtectedArea' && b.kind.kind === 'nearProtectedArea') return a.kind.name === b.kind.name;
    if (a.kind.kind === 'distanceFromWaterway' && b.kind.kind === 'distanceFromWaterway') return true;
    return reasonKindEquals(a.kind, b.kind);
  }
}
